import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { inject } from '@angular/core';
import { Auth } from '../../../core/auth/auth';
import { SidebarService } from '../../../core/services/sidebar';

@Component({
  selector: 'app-sidebar',
  imports: [CommonModule],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.scss',
})
export class Sidebar implements OnInit {
  private readonly auth = inject(Auth);
  private readonly router = inject(Router);
  private readonly sidebarService = inject(SidebarService);

  menulist: any[] = [];
  parentMenulist: any[] = [];
  isSidebarDisabled = false;
  activeParentId: number | null = null;
  isSubSidebarCollapsed = false;

  get user() {
    return this.auth.currentUser();
  }

  ngOnInit(): void {
    this.loadPermissions();

    this.auth.menuList$.subscribe((menus) => {
      if (menus) {
        this.menulist = [...menus];

        const roleType = (this.user?.role_type || '').toLowerCase();
        if (roleType === 'employee' || roleType === 'super_user') {
          const hasHome = this.menulist.some((m: any) => m.name === 'Home' || m.id === 99999);
          if (!hasHome) {
            const homeParent = {
              id: 99999,
              name: 'Home',
              icon: 'hgi hgi-stroke hgi-rounded hgi-home-01',
              link: '',
              can_view: true,
              parentID: 99999,
            };
            this.menulist.unshift(homeParent);
          }
        }

        this.sidebarService.setMenuList(this.menulist);

        this.parentMenulist = [];
        this.menulist.forEach((menu: any) => {
          // can_view === false must never render, even when it still carries
          // action flags (can_save/can_import) from a partial permission row.
          // Absent/undefined can_view (admin-tier "full access" list) stays
          // visible - only an explicit false is filtered.
          if (menu?.can_view === false) return;
          if (!menu?.parentID || menu?.parentID === menu?.id) {
            this.parentMenulist.push(menu);
          }
        });

        if (this.auth.selectedMenuID) {
          const activeMenu: any = this.menulist.find((m: any) => m.id === this.auth.selectedMenuID);
          if (activeMenu) {
            const parentId = this.getPrimaryParentId(activeMenu.id);
            const parentMenu = this.parentMenulist.find((m: any) => m.id === parentId);
            if (parentMenu) {
              this.sidebarService.setSelectedParentId(parentMenu.id);
            }
          }
        }

        this.syncMenuWithRoute();
      }
    });

    this.sidebarService.sidebarDisabled$.subscribe((value) => {
      this.isSidebarDisabled = value;
    });

    this.sidebarService.selectedParentId$.subscribe((id) => {
      this.activeParentId = id;
    });

    this.sidebarService.isSubSidebarCollapsed$.subscribe((value) => {
      this.isSubSidebarCollapsed = value;
    });

    this.router.events.subscribe(() => {
      this.syncMenuWithRoute();
    });
  }

  openSubSidebar(): void {
    this.sidebarService.setCollapsedByUser(false);
    this.sidebarService.setSubSidebarCollapsed(false);

    const activeId = this.sidebarService.getSelectedParentId();
    if (!activeId && this.parentMenulist.length > 0) {
      const firstItem = this.parentMenulist.find((m: any) => m.id !== 99999) || this.parentMenulist[0];
      this.selectParentMenu(firstItem, true);
    }
  }

  private loadPermissions(): void {
    const groupId = Number(this.user?.group_id) || 0;
    this.auth.getPermission(groupId).subscribe();
  }

  getPrimaryParentId(menuId: number): number {
    const menu = this.menulist.find((m: any) => m.id === menuId);
    if (!menu) return menuId;
    if (!menu.parentID || menu.parentID === menu.id) return menu.id;
    return this.getPrimaryParentId(menu.parentID);
  }

  private syncMenuWithRoute(): void {
    const currentPath = this.router.url.split('?')[0].split('#')[0];
    let matchedMenu: any = this.menulist.find(
      (menu: any) => menu.id !== 99999 && menu.link && menu.link === currentPath
    );

    if (!matchedMenu) {
      // Find menu whose link is a prefix of currentPath (e.g. '/ess/employee' matches '/ess/employee/employee')
      matchedMenu = this.menulist.find(
        (menu: any) =>
          menu.id !== 99999 &&
          menu.link &&
          menu.link !== '/' &&
          currentPath.startsWith(menu.link)
      );
    }

    if (!matchedMenu && this.parentMenulist.length > 0) {
      const realParent = this.parentMenulist.find((m: any) => m.id !== 99999);
      if (realParent) {
        this.sidebarService.setSelectedParentId(realParent.id);
        return;
      }
    }

    if (matchedMenu) {
      const parentId = this.getPrimaryParentId(matchedMenu.id);
      this.sidebarService.setSelectedParentId(parentId);

      this.auth.selectedMenuID = matchedMenu.id;
      localStorage.setItem('activeMenuName', matchedMenu.name);
      localStorage.setItem('activeMenu', JSON.stringify(matchedMenu));
    }
  }


  selectParentMenu(item: any, shouldNavigate = true): void {
    console.log("-=-=item", item);

    if (!item || this.isSidebarDisabled) return;
    this.sidebarService.clearHoverTimeout();
    this.sidebarService.setIsHoveredMode(false);
    this.sidebarService.setSelectedParentId(item.id);

    // Retain user's current collapsed/expanded choice
    const isCollapsed = this.sidebarService.getCollapsedByUser();
    this.sidebarService.setSubSidebarCollapsed(isCollapsed);

    if (shouldNavigate) {
      const subMenus = this.menulist.filter(
        (m: any) => m.parentID === item.id && m.id !== m.parentID && m.can_view,
      );

      if (subMenus.length === 0 && item.link && item.link.trim() !== '') {
        this.auth.selectedMenuID = item.id;
        localStorage.setItem('activeMenuName', item.name);
        localStorage.setItem('activeMenu', JSON.stringify(item));
        this.router.navigate([item.link]);
      }
    }
  }

  onItemHover(item: any, event: MouseEvent): void {
    if (!item || this.isSidebarDisabled) return;

    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();

    // Store the hovered item's vertical center
    const hoverCenter = rect.top + rect.height / 2;

    this.sidebarService.setHoverTop(hoverCenter);

    const clickedId = this.sidebarService.getSelectedParentId();
    const isCollapsedByUser = this.sidebarService.getCollapsedByUser();

    if (item.id === clickedId && !isCollapsedByUser) {
      this.sidebarService.clearHoverTimeout();
      this.sidebarService.setIsHoveredMode(false);
      this.sidebarService.setSubSidebarCollapsed(false);
      return;
    }

    this.sidebarService.clearHoverTimeout();
    this.sidebarService.setIsHoveredMode(true);
    this.sidebarService.setHoveredParentId(item.id);
    this.sidebarService.setSubSidebarCollapsed(false);
  }
  onMouseLeave(): void {
    if (this.sidebarService.getIsHoveredMode()) {
      this.sidebarService.startHoverTimeout(() => {
        const isCollapsedByUser = this.sidebarService.getCollapsedByUser();
        if (isCollapsedByUser) {
          this.sidebarService.setSubSidebarCollapsed(true);
          this.sidebarService.setIsHoveredMode(false);
          this.sidebarService.setHoveredParentId(null);
        } else {
          this.sidebarService.setIsHoveredMode(false);
          this.sidebarService.setHoveredParentId(null);
          this.sidebarService.setSubSidebarCollapsed(false);
        }
      }, 300);
    }
  }
}
